import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Popconfirm, message, Typography, Tag, Space, Spin } from 'antd';
import { DeleteOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { deleteSuperDeal, fetchSuperDeals } from '../AddSuperDealAction';
import moment from 'moment';

const { Title, Text } = Typography;

const ListSuperDeals = ({ refreshTrigger = 0 }) => {
  const dispatch = useDispatch();
  const fullState = useSelector(state => state);
  const addSuperDealState = useSelector(state => state.addSuperDeal);
  
  const superDeals = addSuperDealState?.superDeals?.data || [];
  
  const loading = addSuperDealState?.superDeals?.loading || false;
  
  const error = addSuperDealState?.superDeals?.error?.status || 
                addSuperDealState?.error?.status || 
                false;
  
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchSuperDeals());
  }, [dispatch, refreshTrigger]);

  const handleDelete = async (id) => {
    setLocalLoading(true);
    try {
      await dispatch(deleteSuperDeal(id, () => {
        dispatch(fetchSuperDeals());
      }));
    } catch (error) {
      message.error('Failed to delete super deal. Please try again.');
      console.error('Delete error:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchSuperDeals());
  };

  const calculateSavings = (original, discounted) => {
    const originalPrice = parseFloat(original) || 0;
    const discountedPrice = parseFloat(discounted) || 0;
    return originalPrice - discountedPrice;
  };

  const calculateDiscountPercentage = (original, discounted) => {
    const originalPrice = parseFloat(original) || 0;
    const discountedPrice = parseFloat(discounted) || 0;
    if (originalPrice === 0) return 0;
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
  };

  const formatCurrency = (amount, currency = 'LKR') => {
    const numAmount = parseFloat(amount) || 0;
    return `${currency} ${numAmount.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('YYYY-MM-DD');
  };

  const getSpaceName = (record) => {
    return record.space?.spaceName || '';
  };

  const getLocation = (record) => {
    return record.space?.location?.address || 'Location not available';
  };

  const getOriginalPrice = (record) => {
    return record.originalPrice || 0;
  };

  const getDiscountedPrice = (record) => {
    return record.newPrice || 0;
  };

  const getCurrency = (record) => {
    return record.currency || 'LKR';
  };

  const getRecordId = (record) => {
    return record.superDealId;
  };

  const columns = [
    {
      title: 'Space Name',
      dataIndex: 'spaceName',
      key: 'spaceName',
      render: (text, record) => (
        <div>
          <Text strong>{getSpaceName(record)}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {getLocation(record)}
          </Text>
        </div>
      ),
      width: '30%',
    },
    {
      title: 'Charging Type',
      key: 'chargingType',
      render: (_, record) => (
        <div>
          {record.chargingTypeName && (
            <Text style={{ fontSize: '12px', display: 'block' }}>
              {record.chargingTypeName}
            </Text>
          )}
        </div>
      ),
      width: '20%',
    },
    {
      title: 'Pricing',
      key: 'pricing',
      render: (_, record) => {
        const originalPrice = getOriginalPrice(record);
        const discountedPrice = getDiscountedPrice(record);
        const currency = getCurrency(record);
        const savings = calculateSavings(originalPrice, discountedPrice);
        const discountPercent = calculateDiscountPercentage(originalPrice, discountedPrice);
        
        return (
          <div>
            {originalPrice > 0 && (
              <>
                <Text delete style={{ color: '#ff4d4f' }}>
                  {formatCurrency(originalPrice, currency)}
                </Text>
                <br />
              </>
            )}
            <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
              {formatCurrency(discountedPrice, currency)}
            </Text>
          </div>
        );
      },
      width: '20%',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          
          <Popconfirm
            title="Delete Super Deal"
            description="Are you sure you want to delete this super deal?"
            onConfirm={() => handleDelete(getRecordId(record))}
            okText="Yes"
            cancelText="No"
            okType="danger"
            disabled={localLoading}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={localLoading}
              disabled={localLoading}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: '15%',
    },
  ];

  if (error) {
    return (
      <div className="list-super-deals" style={{ marginTop: '24px' }}>
        <Card 
          title="Super Deals"
          className="super-deals-list-card"
        >
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#ff4d4f'
          }}>
            <Text type="danger">
              Failed to load super deals. Please try again.
            </Text>
            <br />
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              style={{ marginTop: '16px' }}
            >
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="list-super-deals" style={{ marginTop: '24px' }}>
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>
              Super Deals ({Array.isArray(superDeals) ? superDeals.length : 0})
            </Title>
            <div>
              <Button 
                type="default" 
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
                size="small"
              >
                Refresh
              </Button>
            </div>
          </div>
        }
        className="super-deals-list-card"
      >
        {loading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px'
          }}>
            <Spin size="large" tip="Loading super deals..." />
          </div>
        ) : !Array.isArray(superDeals) || superDeals.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#999'
          }}>
            <Text>No super deals created yet. Create your first super deal above!</Text>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={superDeals}
            rowKey={(record) => getRecordId(record)}
            loading={localLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} super deals`,
            }}
            size="middle"
          />
        )}
      </Card>
    </div>
  );
};

export default ListSuperDeals;